import React, {Fragment} from 'react'
import {connect} from 'react-redux'
import styles from './ExpandedRidePreview.scss'
import {Map} from 'common/Map'
import {Distance, Elevation, Grade, Speed, Duration} from 'common/units'
import {Loadable} from 'common/Loadable'
import {SHARE_DIALOG, ShareDialog} from 'common/ShareDialog'
import _ from 'lodash'
import moment from 'moment'
import cx from 'classnames'
import {urls} from 'lib/urls'
import {j} from 'lib/textUtils'
import {CTA} from 'common/styleguide/CTA'
import {tripActions, tripSelectors} from 'data/resources/trips'
import {gearSelectors} from 'data/resources/gears'

export class ExpandedRidePreview extends React.PureComponent {
  constructor (props) {
    super(props)

    this.state = {
      dialog: null,
      tripName: null,
      editable: false
    }
  }

  componentDidUpdate (prevProps, prevState) {
    if (this.props.loadableResources && !prevState.tripName) {
      this.setState({tripName: this.props.trip.name})
    }
  }

  hideDialog = () => (
    this.setState({dialog: null})
  )

  showDialog = () => (
    this.setState({dialog: SHARE_DIALOG})
  )

  toggleEditing = () => {
    this.setState({editing: !this.state.editing})
  }

  onRideNameChange = (e) => {
    this.setState({tripName: e.target.value})
  }

  saveTripName = () => {
    const {trip, updateTrip} = this.props
    const {tripName} = this.state

    updateTrip(trip.id, {trip: {name: tripName}})
      .then(r => this.setState({editing: false}))
  }

  render () {
    const {dialog, editing, tripName} = this.state
    const {trip, gear, metricUnits} = this.props
    const {metrics} = trip

    return (
      <div className={styles.ExpandedRidePreview}>
        <div className={styles.left}>
          {editing
            ? <div className={styles.editingTripName}>
              <input value={tripName || ''} onChange={this.onRideNameChange} className={styles.tripNameInput} />
              <span className={styles.saveButton}><CTA secondary margins={false} onClick={this.saveTripName}>Save</CTA></span>
            </div>
            : <h3><span className={styles.tripName} onClick={this.toggleEditing}>{trip.name}</span></h3>}
          <div className={styles.metrics}>
            <div className={styles.metricsSection}>
              <MetricsRow label='Departed' value={moment(trip.departedAt).format('MMM DD, \'YY, HH:mmA')} />
              <MetricsRow label='Starts In' value={j([trip.locality, trip.administrativeArea]) || '-'} />
              <MetricsRow label='Distance' M={Distance} short value={trip.distance} />
              <div className={styles.metricsRow}>
                <div className={styles.metricsCol}>Elevation:</div>
                <div className={styles.metricsCol}>+<Elevation short value={trip.elevationGain} />/-<Elevation short value={trip.elevationLoss} /></div>
              </div>
              {metrics.grade && <MetricsRow label='Max Grade' M={Grade} short value={metrics.grade.max} />}
              {gear && <MetricsRow label='Gear' value={<a href={urls.gear.show(gear.id)}>{gear.name}</a>} />}
            </div>

            {(metrics.duration || metrics.movingTime || metrics.stoppedTime) && <div className={styles.metricsSection}>
              {metrics.duration && <MetricsRow label='Total Duration' title='Total Duration (hh:mm)' M={Duration} value={metrics.duration} bigUnits valueOnly />}
              {metrics.movingTime && <MetricsRow label='Moving Time' title='Moving Time (hh:mm)' M={Duration} value={metrics.movingTime} bigUnits valueOnly />}
              {typeof metrics.stoppedTime === 'number' && <MetricsRow label='Stopped Time' title='Stopped Time (hh:mm)' M={Duration} value={metrics.stoppedTime} bigUnits valueOnly />}
            </div>}

            {(trip.calories || trip.avgWatts) && <div className={styles.metricsSection}>
              {trip.calories && <MetricsRow label='Calories' value={trip.calories} />} {/* this one needs to come from trip for some reason */}
              {trip.avgWatts && <MetricsRow label='Avg Watts' value={trip.avgWatts} />}
            </div>}

            <div className={cx(styles.metricsSection, styles.noBorder)}>
              {metrics.speed && <MetricsRow label='Max Speed' M={Speed} short value={metrics.speed.max} />}
              {metrics.speed && <MetricsRow label='Avg Speed' M={Speed} short value={metrics.speed.avg} />}
            </div>

            {trip.description && <div className={styles.description}>{trip.description}</div>}
          </div>

          <div className={styles.links}>
            <div className={styles.linkWrap}>
              <a href={urls.trips.show(trip.id, trip.privacyCode)}>View Full Details</a>
            </div>
            <div className={styles.linkWrap}>
              <a onClick={this.showDialog}>Share</a>
              {dialog === SHARE_DIALOG &&
                <ShareDialog onRequestClose={this.hideDialog}
                  objectType='trip'
                  objectId={trip.id}
                  objectTitle={trip.name}
                  privacyCode={trip.privacyCode}
                  objectUrl={urls.trips.show(trip.id, trip.privacyCode)} />}
            </div>
          </div>
        </div>

        <div className={styles.right}>
          {trip.isStationary
            ? <img src={urls.staticMap('trip', 'hover_preview@2x', trip.id)}
              style={{width: '100%', height: '100%'}} />
            : <Map experimental
              routes={[trip]}
              metricUnits={metricUnits}
              defaultOverlay={'rwgps'}
              sampleGraph />}
        </div>
      </div>
    )
  }
}

const MetricsRow = ({M, label, value, title, ...metricProps}) => {
  if (M !== undefined) {
    return (value !== null) && <div className={styles.metricsRow}>
      <div className={styles.metricsCol}>{label}:</div>
      <div className={styles.metricsCol}><span data-tip={!!title} title={title}><M value={value} {...metricProps} /></span></div>
    </div>
  } else {
    return value && <div className={styles.metricsRow}>
      <div className={styles.metricsCol}>{label}:</div>
      <div className={styles.metricsCol}><Fragment>{value}</Fragment></div>
    </div>
  }
}

const loadInitialResources = _.memoize(({troute, loadTrip}) => {
  return loadTrip(troute.id)
})

const _LoadableExpandedRidePreview = (props) => {
  return <Loadable
    {...props}
    component={ExpandedRidePreview}
    loadingText='Loading Ride Details'
    resourceLoaded={() => loadInitialResources(props)} />
}

const mapStateToProps = (state, ownProps) => {
  return {
    trip: tripSelectors.find(state, ownProps.troute.id),
    gear: gearSelectors.find(state, ownProps.troute.gearId)
  }
}

const mapDispatchToProps = {
  loadTrip: tripActions.read,
  updateTrip: tripActions.update
}

export const LoadableExpandedRidePreview = connect(
  mapStateToProps,
  mapDispatchToProps
)(_LoadableExpandedRidePreview)
